/**
 * Member & Credits Management Page
 * 
 * Displays user's membership plan, credit balance, transaction history,
 * and provides actions to manage credits and subscription.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import HistoryIcon from '@mui/icons-material/History';
import VerifiedIcon from '@mui/icons-material/Verified';
import { InfoTooltip } from '../components/ui/InfoTooltip';
import PageHeader from '../components/PageHeader';
import RedeemCodeModal from '../components/ui/RedeemCodeModal';
import BuyCreditsModal from '../components/BuyCreditsModal';
import './MemberCreditsPage.css';

interface CreditBreakdown {
  current: number;
  member: number;
  topUp: number;
  bonus: number;
  total: number;
}

interface Transaction {
  id: string;
  type: string;
  time: string;
  amount: number;
  modelId?: string;
  operationType?: string;
  provider?: string;
  description?: string;
  expiresAt?: string;
  createdAt?: string;
}

export const MemberCreditsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { credits, subscription, balance, recentTransactions, getHistory } = useCredits();
  const [transactionHistory, setTransactionHistory] = useState<Transaction[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Load full transaction history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        const history = await getHistory(100);
        setTransactionHistory(history as any);
      } catch (err) {
        console.error('Failed to load transaction history:', err);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [getHistory]);

  const getCreditBreakdown = (): CreditBreakdown => {
    // Use actual subscription data
    const memberCredits = credits?.subscription?.monthlyCreditsRemaining || 0;
    const totalBalance = Math.floor(balance);
    
    // Top-up credits are non-subscription credits (purchased separately)
    // This would be totalBalance - memberCredits
    const topUpCredits = Math.max(0, totalBalance - memberCredits);
    
    return {
      current: totalBalance, // Total available now
      member: memberCredits, // From subscription
      topUp: topUpCredits,   // Purchased separately
      bonus: 0,              // Promotional credits (if any)
      total: totalBalance,
    };
  };

  const formatCredits = (num: number) => {
    return num.toLocaleString('en-US');
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'text_generation': 'Text Generation',
      'image_generation': 'Image Generation', 
      'video_generation': 'Video Generation',
      'member_reward': 'Member Rewards',
      'purchase': 'Credit Purchase',
      'confirm': 'Credit Usage',
      'reserve': 'Credits Reserved',
      'refund': 'Refund',
      'bonus': 'Bonus Credits',
      'subscription': 'Subscription Credits',
      'expiration': 'Credits Expired',
    };
    return labels[type] || type;
  };

  const getModelLabel = (modelId: string): string => {
    if (!modelId || modelId === '-') return '-';
    
    // Handle common model patterns
    const modelLabels: Record<string, string> = {
      'wan-animate-move': 'Wan Animate Move',
      'wan-animate-replace': 'Wan Animate Replace',
      'seedream-4.5': 'Seedream 4.5',
      'imagen-3': 'Imagen 3',
      'veo-3': 'Veo 3',
      'gpt-4': 'GPT-4',
      'gpt-4o': 'GPT-4o',
      'claude-3-sonnet': 'Claude 3 Sonnet',
      'claude-3-haiku': 'Claude 3 Haiku',
    };
    
    // Check for exact match first
    if (modelLabels[modelId]) {
      return modelLabels[modelId];
    }
    
    // Convert kebab-case to Title Case
    return modelId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getOperationLabel = (operationType: string): string => {
    if (!operationType || operationType === '-') return '-';
    
    const operationLabels: Record<string, string> = {
      'video_generation': 'Video',
      'image_generation': 'Image', 
      'text_generation': 'Text',
      'video': 'Video',
      'image': 'Image',
      'text': 'Text',
    };
    
    return operationLabels[operationType] || operationType.replace('_', ' ');
  };

  const isWithinLastSevenDays = (dateString: string | Date): boolean => {
    const transactionDate = new Date(dateString);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return transactionDate >= sevenDaysAgo;
  };

  const getFilteredTransactions = () => {
    // Show only meaningful transactions (exclude internal audit records with amount=0)
    // Shows:
    // - reserve (negative amounts - credit deductions)
    // - refund (positive amounts - credits returned)
    // - bonus (positive amounts - free credits)
    // - purchase (positive amounts - credits bought)
    // Hides:
    // - confirm (amount=0 - internal audit records, not useful for users)
    return recentTransactions.filter(t => 
      t.amount !== 0 && isWithinLastSevenDays(t.createdAt || new Date())
    );
  };

  const getTransactionColor = (amount: number): string => {
    return amount > 0 ? 'positive' : 'negative';
  };

  const breakdown = getCreditBreakdown();

  // Button handlers
  const handleManageSubscription = async () => {
    // Navigate to pricing page with current subscription context
    navigate('/pricing?action=manage');
  };

  const handleRedeemCredits = () => {
    setShowRedeemModal(true);
  };

  const handlePurchaseCredits = () => {
    setShowBuyCreditsModal(true);
  };

  const handleRequestInvoice = async (transactionId: string) => {
    // TODO: Implement invoice request API call
    alert(`Invoice request for transaction ${transactionId} - feature coming soon!`);
  };

  return (
    <div className="member-credits-page">
      <PageHeader />
      
      <div className="member-credits-container">
        <h1 className="member-credits-title">Member & Credit</h1>
        
        {/* Top Row: Membership and Credits side by side */}
        <div className="top-sections">
          {/* Membership Section */}
          <section className="membership-section">
            <h2 className="section-title">My Membership Plan</h2>
          <div className="membership-card">
            {subscription ? (
              <>
                <div className="membership-badge">
                  <VerifiedIcon />
                  {subscription.planName}
                </div>
                <p className="membership-billing">
                  Billed {subscription.billingCycle === 'annual' ? 'Yearly' : 'Monthly'}
                </p>
                <div className="membership-features">
                  <p className="feature-item">✓ {formatCredits(subscription.monthlyCreditsAllocated)} credits per month</p>
                  <p className="feature-item">✓ Access to all AI models</p>
                  <p className="feature-item">✓ Priority support</p>
                </div>
                {/* Check for scheduled downgrade from URL params */}
                {(() => {
                  const urlParams = new URLSearchParams(window.location.search);
                  const changeType = urlParams.get('type');
                  const effectiveDate = urlParams.get('effectiveDate');
                  
                  if (changeType === 'downgrade' && effectiveDate) {
                    return (
                      <div className="scheduled-change-notice" style={{
                        background: 'var(--color-bg-hover)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-sm)',
                        marginTop: 'var(--spacing-sm)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        📅 <strong>Scheduled Change:</strong> Your plan will change at the end of your billing period on {formatDate(effectiveDate)}. You&apos;ll keep your current credits until then.
                      </div>
                    );
                  }
                  if (changeType === 'upgrade') {
                    return (
                      <div className="upgrade-notice" style={{
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: '1px solid var(--color-accent-primary)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-sm)',
                        marginTop: 'var(--spacing-sm)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-accent-primary)'
                      }}>
                        ✨ Plan upgraded! Your new credits are available immediately.
                      </div>
                    );
                  }
                  return null;
                })()}
                <p className="membership-renewal">
                  Renew on {formatDate(subscription.currentPeriodEnd)} • {subscription.cancelAtPeriodEnd ? 'Cancels at period end' : 'Auto-renews'}
                </p>
              </>
            ) : (
              <>
                <div className="membership-badge">
                  <VerifiedIcon />
                  Free Plan
                </div>
                <p className="membership-billing">No payment required</p>
                <div className="membership-features">
                  <p className="feature-item">✓ Limited credits to try out the platform</p>
                  <p className="feature-item">✓ Access to all AI models</p>
                  <p className="feature-item">✓ Community support</p>
                </div>
              </>
            )}
          </div>
          <div className="membership-actions">
            {subscription ? (
              <button 
                className="membership-action-btn primary"
                onClick={handleManageSubscription}
              >
                Manage Subscription
              </button>
            ) : (
              <button 
                className="membership-action-btn primary"
                onClick={() => navigate('/pricing')}
              >
                Upgrade Plan
              </button>
            )}
          </div>
        </section>

          {/* Credit Balance Section */}
          <section className="credit-balance-section">
            <h2 className="section-title">My Credit Balance</h2>
          <div className="credit-balance-breakdown">
            <div className="credit-row">
              <span className="credit-label">
                Current
                <InfoTooltip content="Credits available for immediate use. These are consumed first when using AI features." />
              </span>
              <span className="credit-value">{formatCredits(breakdown.current)}</span>
            </div>
            <div className="credit-row">
              <span className="credit-label">
                Member
                <InfoTooltip content="Monthly credits included with your membership plan. These renew each billing cycle." />
              </span>
              <span className="credit-value">{formatCredits(breakdown.member)}</span>
            </div>
            <div className="credit-row">
              <span className="credit-label">
                Top-up
                <InfoTooltip content="Additional credits purchased separately. These never expire and roll over each month." />
              </span>
              <span className="credit-value">{formatCredits(breakdown.topUp)}</span>
            </div>
            <div className="credit-row separator">
              <span className="credit-label">
                Total
                <InfoTooltip content="Sum of all available credits. This is your total balance for using AI features." />
              </span>
              <span className="credit-value total">{formatCredits(breakdown.total)}</span>
            </div>
          </div>
          <div className="credit-actions">
            <button className="credit-action-btn secondary" onClick={handleRedeemCredits}>
              <CardGiftcardIcon />
              Redeem Credits
            </button>
            <button className="credit-action-btn primary" onClick={handlePurchaseCredits}>
              <CreditCardIcon />
              Purchase Credits
            </button>
          </div>
        </section>
        </div>

        {/* Credit Details Section */}
        <section className="credit-details-section">
          <div className="details-tabs">
            <button
              className={`tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              <HistoryIcon />
              Credit Details
            </button>
            <button
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <HistoryIcon />
              Transaction History
            </button>
          </div>

          {activeTab === 'details' && (
            <div className="tab-content">
              <div className="tab-subtitle">Last 7 days</div>
              <div className="details-header">
                <div className="header-cell">Type</div>
                <div className="header-cell">Model</div>
                <div className="header-cell">Operation</div>
                <div className="header-cell">Time</div>
                <div className="header-cell">Amount</div>
              </div>
              <div className="details-list">
                {getFilteredTransactions().length > 0 ? (
                  getFilteredTransactions().slice(0, 10).map((transaction, idx) => (
                    <div key={`${transaction.id || idx}`} className={`detail-row color-${getTransactionColor(transaction.amount || 0)}`}>
                      <div className="cell">{getTransactionTypeLabel(transaction.type || '')}</div>
                      <div className="cell">{getModelLabel(transaction.modelId || '')}</div>
                      <div className="cell">{getOperationLabel(transaction.operationType || '')}</div>
                      <div className="cell">{formatDate(transaction.createdAt || new Date())}</div>
                      <div className="cell amount">
                        {transaction.amount > 0 ? '+' : ''}{formatCredits(Math.abs(transaction.amount || 0))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No transactions in the last 7 days</div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="tab-content">
              <div className="history-header">
                <div className="header-cell">Product Name</div>
                <div className="header-cell">Payment Time</div>
                <div className="header-cell">Payment Amount</div>
                <div className="header-cell">Order Number</div>
                <div className="header-cell">Action</div>
              </div>
              <div className="history-list">
                {transactionHistory.length > 0 ? (
                  transactionHistory
                    .filter((t: any) => t.type === 'purchase')
                    .map((transaction: any, idx) => (
                      <div key={`${transaction.id || idx}`} className="history-row">
                        <div className="cell">{getTransactionTypeLabel(transaction.type || '')}</div>
                        <div className="cell">{formatDate(transaction.createdAt || new Date())}</div>
                        <div className="cell">{formatCredits(Math.abs(transaction.amount || 0))}</div>
                        <div className="cell order-id">{transaction.orderId || 'N/A'}</div>
                        <div className="cell action">
                          <button className="action-link" onClick={() => handleRequestInvoice(transaction.id)}>Request Invoice</button>
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="empty-state">No purchase history</div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Footer with Legal Links */}
      <footer className="member-credits-footer">
        <div className="member-credits-footer-content">
          <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          <span className="member-credits-footer-divider">•</span>
          <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span className="member-credits-footer-divider">•</span>
          <a href="/cookies.html" target="_blank" rel="noopener noreferrer">
            Cookie Policy
          </a>
        </div>
      </footer>

      {/* Modals */}
      <RedeemCodeModal 
        isOpen={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
      />
      <BuyCreditsModal 
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
      />
    </div>
  );
};

export default MemberCreditsPage;
